import React, { useState } from 'react';

/* SmartImage - drop-in replacement for <img> that automatically serves a
 * WebP version when one exists alongside the source file (e.g. /foo.jpg
 * + /foo.webp). The browser picks the lightest format it supports.
 *
 * Why a wrapper:
 *   • A naive build pipeline doesn't know about per-image WebP siblings.
 *   • Hand-writing <picture> at every call site is verbose.
 *   • Without WebP, a 200KB JPG still ships ~200KB; with WebP, the same
 *     visual ships at 50–110KB, which compounds across a long page.
 *
 * Feb 2026 perceived-speed upgrade:
 *   • Each image now starts with a soft gradient + shimmer placeholder
 *     painted by the browser itself (no extra request) so users see
 *     something pleasant immediately instead of a blank rectangle.
 *   • Once the actual bitmap decodes, opacity ramps from 0 → 1 over
 *     320 ms via an onLoad handler. This is the "Cloudflare Pages /
 *     Vercel / Next.js" feel - images don't snap in, they bloom in.
 *   • The placeholder gradient uses a slow horizontal animation so
 *     long loads look intentional rather than stuck.
 *
 * Usage:
 *   <SmartImage src="/blog-images/foo.jpg" alt="..." width={1200} height={750} />
 *
 * Props mirror <img>. Pass `priority` for above-the-fold images so we mark
 * them eager + high fetchpriority and skip the lazy default.
 */
function deriveWebpSrc(src) {
  if (!src || typeof src !== 'string') return null;
  if (src.startsWith('data:') || src.startsWith('http')) return null;
  const dot = src.lastIndexOf('.');
  if (dot < 0) return null;
  const ext = src.slice(dot + 1).toLowerCase();
  // Only swap formats that actually have a WebP sibling generated for them.
  if (!['jpg', 'jpeg', 'png'].includes(ext)) return null;
  return src.slice(0, dot) + '.webp';
}

export default function SmartImage({
  src,
  alt = '',
  className,
  width,
  height,
  priority = false,
  loading,
  decoding = 'async',
  fetchpriority,
  sizes,
  style,
  ...rest
}) {
  const webp = deriveWebpSrc(src);
  const finalLoading = loading || (priority ? 'eager' : 'lazy');
  const finalFetchPriority = fetchpriority || (priority ? 'high' : 'auto');

  // `loaded` flips true once the bitmap is decoded. While false, the img
  // is invisible and the user sees only the shimmer placeholder behind.
  const [loaded, setLoaded] = useState(false);

  const placeholderStyle = {
    // Light neutral gradient + a soft purple wash that matches the brand.
    // The animation is defined as `.smart-img-shimmer` in App.css so we
    // can ship a single keyframe + respect prefers-reduced-motion.
    backgroundColor: '#f3f4f6',
    backgroundImage:
      'linear-gradient(110deg, rgba(241,245,249,0) 30%, rgba(216,180,254,0.35) 50%, rgba(241,245,249,0) 70%)',
    backgroundSize: '220% 100%',
    backgroundRepeat: 'no-repeat',
  };

  const imgStyle = {
    ...placeholderStyle,
    opacity: loaded ? 1 : 0,
    transition: 'opacity 320ms ease-out',
    ...style,
  };

  const commonImgProps = {
    src,
    className: `${className || ''} ${loaded ? '' : 'smart-img-shimmer'}`.trim(),
    width,
    height,
    loading: finalLoading,
    decoding,
    fetchpriority: finalFetchPriority,
    sizes,
    onLoad: () => setLoaded(true),
    // If the bitmap is already in the disk cache the onLoad may fire
    // before React attaches. Catching errors avoids a "stuck shimmer".
    onError: () => setLoaded(true),
    style: imgStyle,
    ...rest,
  };

  // No webp candidate? Just render a normal img.
  if (!webp) return <img {...commonImgProps} alt={alt} />;

  return (
    <picture>
      <source srcSet={webp} type="image/webp" sizes={sizes} />
      <img {...commonImgProps} alt={alt} />
    </picture>
  );
}
